import React, { useEffect, useState, useRef } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaArrowLeft,
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaUserSecret,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSyncAlt,
  FaPaperPlane,
  FaPlay,
  FaBolt,
  FaRobot,
  FaUser,
  FaKey,
  FaTrashAlt,
  FaSignInAlt,
} from 'react-icons/fa';

import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechTabs,
  TechButton,
  TechButtonGhost,
  TechTextarea,
  TechInput,
  StatusPill,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  TechChip,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import {
  getDigitalProfile,
  runProfileAction,
  chatToProfile,
  setProfileSessionid,
  deleteDigitalProfile,
  listProfileTasks,
  clearProfileTasks,
  updateProfileCredentials,
  reloginProfile,
  browserLogin,
  browserLoginCode,
  autoExtractCookies,
} from '../../services/digitalProfilesService';
import StepProgress from '../common/StepProgress';
import ConfirmModal from './ConfirmModal';

const PLATFORM_META = {
  instagram: { color: '#E1306C', Icon: FaInstagram, label: 'Instagram' },
  facebook:  { color: '#1877F2', Icon: FaFacebook, label: 'Facebook' },
  x:         { color: '#000000', Icon: FaTwitter, label: 'X / Twitter' },
  twitter:   { color: '#000000', Icon: FaTwitter, label: 'X / Twitter' },
  linkedin:  { color: '#0A66C2', Icon: FaUserSecret, label: 'LinkedIn' },
};

const ActivityList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const ActivityRow = styled.li`
  display: grid;
  grid-template-columns: 90px 140px 1fr 70px 80px;
  gap: 0.55rem;
  padding: 0.55rem 0.75rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${(p) => p.$color || accent};
  border-radius: 4px;
  font-family: ${mono};
  font-size: 0.72rem;
  align-items: center;

  .when { color: var(--text-disabled); }
  .action { color: var(--text-primary); font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; }
  .target { color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .source { color: var(--text-disabled); }
  .status {
    text-align: right;
    text-transform: uppercase; letter-spacing: 0.12em;
    &.ok { color: var(--alert-success, #38d39f); }
    &.error { color: var(--alert-error, #ff5470); }
    &.skipped { color: var(--alert-warning, #f59e0b); }
  }
  .errmsg {
    grid-column: 1 / -1;
    margin-top: 0.4rem;
    padding: 0.4rem 0.55rem;
    background: rgba(255, 84, 112, 0.07);
    border-left: 2px solid var(--alert-error, #ff5470);
    border-radius: 3px;
    color: var(--alert-error, #ff5470);
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.7rem;
    line-height: 1.45;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th, td {
    text-align: left;
    padding: 0.5rem 0.7rem;
    border-bottom: 1px solid ${borderSoft};
  }
  th {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    color: var(--text-disabled);
    text-transform: uppercase;
    background: ${panelDeep};
  }
  .ok { color: var(--alert-success, #38d39f); }
  .err { color: var(--alert-error, #ff5470); }
`;

const ChatBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 480px;
  overflow-y: auto;
  padding: 0.5rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
`;

const ChatMsg = styled.div`
  display: flex;
  gap: 0.6rem;
  padding: 0.55rem 0.8rem;
  background: ${(p) => p.$role === 'user' ? 'rgba(0, 180, 216, 0.08)' : 'transparent'};
  border-radius: 6px;
  border-left: 2px solid ${(p) => p.$role === 'user' ? accent : 'rgba(255,255,255,0.1)'};
  font-size: 0.86rem;
  color: var(--text-primary);
  line-height: 1.45;

  .icon {
    flex-shrink: 0;
    width: 28px; height: 28px; border-radius: 50%;
    background: rgba(255,255,255,0.05);
    color: ${(p) => p.$role === 'user' ? accent : 'var(--text-secondary)'};
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.78rem;
  }

  .body {
    flex: 1;
    .role { font-family: ${mono}; font-size: 0.58rem; color: var(--text-disabled); text-transform: uppercase; letter-spacing: 0.16em; }
    .content { margin-top: 2px; white-space: pre-wrap; word-break: break-word; }
    .action {
      margin-top: 4px;
      font-family: ${mono};
      font-size: 0.65rem;
      color: var(--text-disabled);
      padding: 3px 6px;
      background: rgba(255,255,255,0.03);
      border-radius: 3px;
      display: inline-block;
    }
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
};

const fmtAbs = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const TABS = [
  { id: 'activity', label: 'Actividad' },
  { id: 'tasks', label: 'Tareas' },
  { id: 'stats', label: 'Stats' },
  { id: 'chat', label: 'Chatbot' },
];

const DigitalProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('activity');

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const chatEndRef = useRef(null);

  // Quick action state
  const [actionBusy, setActionBusy] = useState(null);

  // Confirmación de borrado (modal en vez de window.confirm)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Filtro del log de actividad
  const [onlyErrors, setOnlyErrors] = useState(false);

  // Lista de tareas del agente
  const [tasks, setTasks] = useState([]);

  // Credenciales / auto-login
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);
  // Correo de verificación (IMAP)
  const [emailAddr, setEmailAddr] = useState('');
  const [emailImapPass, setEmailImapPass] = useState('');
  const [emailImapHost, setEmailImapHost] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connectStage, setConnectStage] = useState(null); // 'reuse' | 'browser'
  const [credMsg, setCredMsg] = useState(null);
  // Login con navegador (server) + código de verificación
  const [challenge, setChallenge] = useState(null); // { challengeId, hint }
  const [verifCode, setVerifCode] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);

  // Auto-extract cookies — step progress
  const [extractSteps, setExtractSteps] = useState([]);
  const [extractStepStatus, setExtractStepStatus] = useState({});
  const EXTRACT_STEPS = [
    { key: 'browser', label: 'Abriendo navegador con Playwright' },
    { key: 'navigate', label: 'Navegando a la página de login' },
    { key: 'credentials', label: 'Ingresando credenciales' },
    { key: 'captcha', label: 'Detectando / resolviendo captcha' },
    { key: 'phone', label: 'Verificación por teléfono (si aplica)' },
    { key: 'cookies', label: 'Extrayendo cookies de sesión' },
    { key: 'save', label: 'Guardando sesión en la base de datos' },
  ];

  const load = () => {
    setLoading(true);
    getDigitalProfile(id)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const loadTasks = () => {
    listProfileTasks(id).then((d) => setTasks(d.tasks || [])).catch(() => {});
  };
  useEffect(loadTasks, [id]);

  const handleClearTasks = async () => {
    try { await clearProfileTasks(id); loadTasks(); } catch { /* noop */ }
  };

  useEffect(() => {
    setLoginUser((cur) => cur || data?.account?.login_user || '');
    setEmailAddr((cur) => cur || data?.account?.email_address || data?.account?.login_user || '');
    setEmailImapHost((cur) => cur || data?.account?.email_imap_host || '');
  }, [data]);

  const handleSaveCreds = async () => {
    setSavingCreds(true); setCredMsg(null);
    try {
      const payload = {};
      if (loginUser.trim()) payload.login_user = loginUser.trim();
      if (loginPass) payload.password = loginPass;
      if (totpSecret.trim()) payload.totp_secret = totpSecret.trim();
      if (Object.keys(payload).length === 0) {
        setCredMsg({ ok: false, text: 'Ingresá usuario, contraseña y/o el secret de 2FA.' });
        return;
      }
      await updateProfileCredentials(id, payload);
      setLoginPass(''); setTotpSecret('');
      setCredMsg({ ok: true, text: 'Credenciales guardadas (cifradas en el server).' });
      load();
    } catch (err) {
      setCredMsg({ ok: false, text: friendlyError(err) });
    } finally { setSavingCreds(false); }
  };

  const handleSaveEmail = async () => {
    setSavingEmail(true); setEmailMsg(null);
    try {
      const payload = {};
      if (emailAddr.trim()) payload.email_address = emailAddr.trim();
      if (emailImapPass) payload.email_imap_password = emailImapPass;
      if (emailImapHost.trim()) payload.email_imap_host = emailImapHost.trim();
      if (Object.keys(payload).length === 0) {
        setEmailMsg({ ok: false, text: 'Ingresá el correo y/o el app-password IMAP.' });
        return;
      }
      await updateProfileCredentials(id, payload);
      setEmailImapPass('');
      setEmailMsg({ ok: true, text: 'Correo de verificación guardado (app-password cifrado en el server).' });
      load();
    } catch (err) {
      setEmailMsg({ ok: false, text: friendlyError(err) });
    } finally { setSavingEmail(false); }
  };

  // Login con navegador interactivo (server): abre un navegador real, loguea y, si
  // IG/FB pide código, deja el navegador esperando y surge el campo de verificación.
  const runBrowserLogin = async () => {
    setConnectStage('browser');
    const br = await browserLogin(id);
    if (br.ok) {
      setCredMsg({ ok: true, text: br.message || 'Login con navegador OK.' });
      return;
    }
    if (br.challenge) {
      setChallenge({ challengeId: br.challengeId, hint: br.hint });
      setCredMsg({ ok: true, text: br.hint || 'IG/FB pidió un código de verificación. Ingresalo abajo.' });
      return;
    }
    setCredMsg({ ok: false, text: br.error || 'No se pudo iniciar el login con navegador' });
  };

  // Botón único e inteligente. silent=true lo usa el auto-conectar al abrir (no ensucia
  // con errores genéricos, pero sí muestra éxito y el campo de código).
  // - Si la cuenta YA está en challenge_required y hay password → va DIRECTO al login
  //   con navegador interactivo. Evita re-correr la cascada (curl + playwright /login),
  //   que serían intentos de login reales extra = más riesgo de checkpoint/ban.
  // - Si no, corre la cascada segura (reuse → curl → navegador) y solo escala al
  //   navegador interactivo si IG/FB pide verificación.
  const handleConnect = async ({ silent = false } = {}) => {
    const acc = data?.account;
    setConnecting(true); setConnectStage(null);
    if (!silent) setCredMsg(null);
    setChallenge(null); setVerifCode('');
    try {
      if (acc?.status === 'challenge_required' && acc?.has_password) {
        await runBrowserLogin();
        load();
        return;
      }
      setConnectStage('reuse');
      const r = await reloginProfile(id);
      if (r.ok) {
        const tierTxt = { reuse: ' (sesión reutilizada)', login: ' (login programático)', playwright: ' (navegador real)' }[r.tier] || '';
        setCredMsg({ ok: true, text: (r.message || 'Sesión activa.') + tierTxt });
        load();
        return;
      }
      if (r.status === 'challenge_required' && acc?.has_password) {
        await runBrowserLogin();
        load();
        return;
      }
      if (!silent || r.status === 'challenge_required') {
        setCredMsg({ ok: false, text: (r.status === 'challenge_required' ? 'Checkpoint/2FA: ' : '') + (r.error || 'No se pudo iniciar sesión') });
      }
      load();
    } catch (err) {
      if (!silent) setCredMsg({ ok: false, text: friendlyError(err) });
    } finally { setConnecting(false); setConnectStage(null); }
  };

  const handleSubmitCode = async () => {
    if (!challenge || !verifCode.trim()) return;
    setCodeBusy(true); setCredMsg(null);
    try {
      const r = await browserLoginCode(id, challenge.challengeId, verifCode.trim());
      if (r.ok) {
        setChallenge(null); setVerifCode('');
        setCredMsg({ ok: true, text: r.message || 'Código aceptado — sesión activa.' });
        load();
      } else {
        setCredMsg({ ok: false, text: r.error || 'Código no aceptado' });
        if (!r.challenge) setChallenge(null); // ya no hay campo de código
      }
    } catch (err) {
      setCredMsg({ ok: false, text: friendlyError(err) });
    } finally { setCodeBusy(false); }
  };

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Auto-conectar al abrir el perfil: intenta reusar la sesión guardada (Tier 0, sin
  // riesgo de checkpoint) y, si está vencida, escala el login UNA sola vez por perfil.
  // Guardas anti-hammering: no toca si la cuenta ya está activa, está en cooldown, o
  // está esperando un código de verificación.
  const autoConnectedFor = useRef(null);
  useEffect(() => {
    const acc = data?.account;
    if (!acc || autoConnectedFor.current === acc.id) return;
    autoConnectedFor.current = acc.id;
    const canLogin = acc.has_password || acc.has_sessionid;
    if (acc.status !== 'active' && !acc.in_cooldown && acc.status !== 'challenge_required' && canLogin) {
      handleConnect({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Auto-refresco: mientras haya tareas encoladas o corriendo, refresca actividad y
  // tareas solo (las tareas se ejecutan en background respetando los límites anti-ban).
  useEffect(() => {
    const pending = tasks.some((t) => ['pending', 'queued', 'running'].includes(t.status));
    if (!pending) return undefined;
    const iv = setInterval(() => { loadTasks(); load(); }, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const handleLoadSessionid = async () => {
    const platform = data?.account?.platform;
    const username = data?.account?.username;
    let help;
    if (platform === 'facebook') {
      help = `Para sacar las cookies de @${username} (Facebook):

1. Abrí facebook.com en browser/incógnito.
2. Iniciá sesión con la cuenta.
3. F12 → Application → Cookies → facebook.com
4. Copiá las dos cookies clave juntas en este formato:
   c_user=TU_USER_ID; xs=TU_XS_COMPLETO; fr=...; datr=...

Pegá esa línea acá:`;
    } else if (platform === 'linkedin') {
      help = `Para sacar las cookies de @${username} (LinkedIn):

1. Abrí linkedin.com en browser/incógnito.
2. Iniciá sesión con la cuenta.
3. F12 → Application → Cookies → linkedin.com
4. Copiá las cookies clave (li_at es la crítica):
   li_at=AQE...; JSESSIONID=ajax:123...

Pegá esa línea acá:`;
    } else if (platform === 'x' || platform === 'twitter') {
      help = `Para cargar sesión de @${username} (X / Twitter):

1. Abrí x.com en browser/incógnito.
2. Iniciá sesión con la cuenta.
3. F12 → Application → Cookies → x.com
4. Copiá las cookies clave en este formato:
   auth_token=...; ct0=...; twid=...

Pegá esa línea acá:`;
    } else {
      help = `Para sacar el sessionid de @${username} (Instagram):

1. Abrí instagram.com en un browser/incógnito.
2. Iniciá sesión con la cuenta.
3. F12 → Application → Cookies → instagram.com
4. Copiá el value de la fila "sessionid".

Pegalo acá:`;
    }
    const sessionid = window.prompt(help, '');
    if (!sessionid) return;
    try {
      await setProfileSessionid(id, sessionid.trim());
      alert('Sessionid cargado. Cuenta activa.');
      load();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDigitalProfile(id);
      navigate('/perfiles-digitales');
    } catch (err) {
      alert(friendlyError(err));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleQuickAction = async (action, target = null) => {
    setActionBusy(action);
    try {
      await runProfileAction(id, action, target);
      load();
    } catch (err) {
      alert(friendlyError(err));
    } finally {
      setActionBusy(null);
    }
  };

  const handleChat = async (e) => {
    e.preventDefault();
    const msg = chatInput.trim();
    if (!msg) return;
    setChatInput('');
    setChatMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setChatBusy(true);
    try {
      const res = await chatToProfile(id, msg);
      setChatMessages((prev) => [...prev, {
        role: 'assistant',
        content: res.reply || '(sin respuesta)',
        action: res.action,
        target: res.target,
        action_status: res.action_status,
        action_result: res.action_result,
      }]);
      load();
      loadTasks();
    } catch (err) {
      setChatMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Error: ${friendlyError(err)}`,
      }]);
    } finally {
      setChatBusy(false);
    }
  };

  if (loading) {
    return (
      <TechShell>
        <TechPageHeader title="Cargando…" dossier="DIGI://PROFILE" />
        <Loading>Cargando perfil</Loading>
      </TechShell>
    );
  }

  if (error || !data) {
    return (
      <TechShell>
        <TechPageHeader title="Perfil no encontrado" dossier="DIGI://PROFILE" />
        <ErrorBox>{error || 'No se encontró'}</ErrorBox>
        <TechButtonGhost as={Link} to="/perfiles-digitales" style={{ marginTop: '1rem' }}>
          <FaArrowLeft /> Volver
        </TechButtonGhost>
      </TechShell>
    );
  }

  const { account, activity, stats } = data;
  const errorCount = activity.filter((a) => a.status === 'error').length;
  // Conteos reales de 24h desde el log de actividad (las columnas requests_24h/
  // errors_24h de dummy_accounts no las actualizan las acciones de perfiles).
  const since24h = Date.now() - 24 * 60 * 60 * 1000;
  const acts24h = activity.filter((a) => new Date(a.created_at).getTime() > since24h);
  const reqs24h = acts24h.length;
  const errs24h = acts24h.filter((a) => a.status === 'error').length;
  const shownActivity = onlyErrors ? activity.filter((a) => a.status === 'error') : activity;
  const meta = PLATFORM_META[account.platform] || { color: accent, Icon: FaUserSecret, label: account.platform };
  const Icon = meta.Icon;

  return (
    <TechShell>
      <TechPageHeader
        title={account.display_name || `@${account.username}`}
        subtitle={`${meta.label} · @${account.username} · status ${account.status}`}
        dossier={`DIGI://${account.platform.toUpperCase()}/${account.id}`}
        actions={
          <>
            <TechButtonGhost as={Link} to="/perfiles-digitales">
              <FaArrowLeft /> Volver
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={handleLoadSessionid} title="Cargar/actualizar sessionid">
              <FaKey /> {account.has_sessionid ? 'Renovar sessionid' : 'Cargar sessionid'}
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={load}>
              <FaSyncAlt /> Refrescar
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={() => setConfirmDelete(true)} title="Eliminar perfil" style={{ color: 'var(--alert-error)' }}>
              <FaTrashAlt />
            </TechButtonGhost>
          </>
        }
      >
        {account.has_sessionid
          ? <StatusPill style={{ color: 'var(--alert-success, #38d39f)' }}>sessionid OK ({fmtAbs(account.sessionid_at)})</StatusPill>
          : <StatusPill style={{ color: 'var(--alert-error)' }}>sin sessionid</StatusPill>}
        {account.has_totp && (
          <StatusPill style={{ color: 'var(--alert-success, #38d39f)' }}>2FA TOTP ✓</StatusPill>
        )}
        {account.has_email_imap && (
          <StatusPill style={{ color: 'var(--alert-success, #38d39f)' }}>email IMAP ✓</StatusPill>
        )}
        {account.in_cooldown && (
          <StatusPill style={{ color: 'var(--alert-warning, #f59e0b)' }}>
            cooldown hasta {fmtAbs(account.cooldown_until)}
          </StatusPill>
        )}
        {account.health?.label !== 'unused' && (
          <StatusPill>health <strong>{account.health.score}%</strong></StatusPill>
        )}
      </TechPageHeader>

      <StatStrip min="170px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaBolt /> Acciones (24h)</StatLabel>
          <StatValue>{reqs24h}</StatValue>
          <StatSub>{errs24h} errores</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCheckCircle /> Health score</StatLabel>
          <StatValue style={{
            color: account.health?.label === 'healthy' ? 'var(--alert-success, #38d39f)' :
                   account.health?.label === 'critical' ? 'var(--alert-error)' :
                   account.health?.label === 'degraded' ? 'var(--alert-warning, #f59e0b)' :
                   'var(--text-secondary)',
          }}>
            {account.health?.label === 'unused' ? '—' : `${account.health.score}%`}
          </StatValue>
          <StatSub>{account.health?.label}</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel>Proxy</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>
            {account.proxy_id || 'sin asignar'}
          </StatValue>
          <StatSub>Webshare</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel>Última actividad</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>
            {fmtRel(account.last_used_at)}
          </StatValue>
          <StatSub>{fmtAbs(account.last_used_at)}</StatSub>
        </StatTile>
      </StatStrip>

      {account.last_error && (
        <div style={{
          padding: '0.7rem 0.9rem',
          background: panelDeep,
          border: `1px solid ${borderSoft}`,
          borderLeft: '3px solid var(--alert-error, #ff5470)',
          borderRadius: 6,
          marginBottom: '1rem',
          fontSize: '0.82rem',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}>
          <strong style={{ color: 'var(--alert-error, #ff5470)' }}>Último error</strong>
          {' '}({fmtRel(account.last_login_at || account.last_used_at)} atrás):{' '}
          <code style={{ fontFamily: mono, color: 'var(--alert-error, #ff5470)', wordBreak: 'break-word' }}>{account.last_error}</code>
        </div>
      )}

      <TechSection title="Acceso · auto-login" style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.7rem', lineHeight: 1.5 }}>
          Guardá el <strong>email/usuario</strong> y la <strong>contraseña</strong> (se cifran en el server). Al abrir el perfil <strong>se reconecta solo</strong> (reusando la sesión guardada cuando sigue viva), y la <strong>renueva sola</strong> cuando una acción detecta la sesión vencida. El botón corre toda la cascada: reusar → re-login → navegador. Si cargás el <strong>secret de 2FA (TOTP)</strong>, el server <strong>genera y resuelve el código de verificación solo</strong> —sin que ingreses nada, sin extensión—.
          {account.platform === 'facebook' && (
            <> <span style={{ color: 'var(--alert-warning, #f59e0b)' }}>Ojo:</span> Facebook suele pedir verificación (checkpoint) desde el server; si pasa, cargá las cookies manualmente con “Renovar sessionid”.</>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <TechInput
            placeholder="email / usuario / teléfono de login"
            value={loginUser}
            onChange={(e) => setLoginUser(e.target.value)}
            style={{ flex: '1 1 220px' }}
          />
          <TechInput
            type="password"
            placeholder={account.has_password ? '•••••• (guardada — dejá vacío para no cambiar)' : 'contraseña'}
            value={loginPass}
            onChange={(e) => setLoginPass(e.target.value)}
            style={{ flex: '1 1 180px' }}
          />
          <TechInput
            placeholder={account.has_totp ? '2FA TOTP ✓ (dejá vacío para no cambiar)' : 'secret 2FA / otpauth:// (opcional)'}
            value={totpSecret}
            onChange={(e) => setTotpSecret(e.target.value)}
            style={{ flex: '1 1 200px' }}
            title="Pegá el secret del autenticador (o la URI otpauth://...). El server genera el código de verificación solo."
          />
          <TechButtonGhost type="button" onClick={handleSaveCreds} disabled={savingCreds}>
            <FaKey /> {savingCreds ? 'Guardando…' : 'Guardar credenciales'}
          </TechButtonGhost>
          <TechButton
            type="button"
            onClick={() => handleConnect()}
            disabled={connecting || (!account.has_password && !account.has_sessionid)}
            title="Reusa la sesión guardada; si está vencida re-loguea, y si IG/FB pide un código de verificación lo ingresás acá abajo"
          >
            <FaSignInAlt /> {connecting
              ? (connectStage === 'browser' ? 'Abriendo navegador…' : 'Conectando…')
              : 'Conectar sesión'}
          </TechButton>
          <TechButtonGhost
            type="button"
            onClick={async () => {
              setCredMsg(null);
              setExtractSteps(EXTRACT_STEPS);
              setExtractStepStatus({});
              setCredMsg({ ok: true, text: 'Extrayendo cookies automáticamente via navegador… puede tardar ~30s.' });
              // Animar pasos estimados
              const delay = 4000;
              EXTRACT_STEPS.forEach((s, i) => {
                setTimeout(() => setExtractStepStatus((p) => ({ ...p, [s.key]: 'running' })), i * delay);
                setTimeout(() => setExtractStepStatus((p) => ({ ...p, [s.key]: 'done' })), (i + 1) * delay - 200);
              });
              try {
                const r = await autoExtractCookies(id);
                // Ajustar estado real según resultado
                setExtractStepStatus((p) => ({
                  ...p,
                  browser: 'done', navigate: 'done', credentials: 'done',
                  ...(r.ok ? {
                    captcha: 'done', phone: 'done', cookies: 'done', save: 'done',
                  } : {
                    ...(r.error?.includes('captcha') ? { captcha: 'error' } : { captcha: 'done' }),
                    ...(r.error?.includes('teléfono') || r.error?.includes('phone') ? { phone: 'error' } : { phone: 'done' }),
                    ...(r.challenge ? { cookies: 'error', save: 'error' } : {}),
                    ...(r.error && !r.challenge ? { save: 'error' } : {}),
                  }),
                }));
                if (r.ok) {
                  setCredMsg({ ok: true, text: `Cookies extraídas y guardadas. Sesión activa.` });
                  load();
                } else {
                  setCredMsg({ ok: false, text: r.error || 'No se pudieron extraer las cookies.' });
                }
              } catch (err) {
                setExtractStepStatus((p) => ({ ...p, save: 'error' }));
                setCredMsg({ ok: false, text: friendlyError(err) });
              }
            }}
            disabled={connecting || !account.has_password}
            title="Login automático con Playwright: abre un navegador real, loguea con las credenciales guardadas y extrae las cookies de sesión"
          >
            <FaRobot /> Extraer cookies
          </TechButtonGhost>
        </div>

        {connecting && connectStage === 'browser' && (
          <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-disabled)', lineHeight: 1.5 }}>
            El server está abriendo un navegador real y logueando… puede tardar ~1 min. Si IG/FB pide un código, va a aparecer un campo acá abajo.
          </div>
        )}

        {challenge && (
          <div style={{ marginTop: 12, padding: '0.7rem 0.9rem', background: panelDeep, border: `1px solid ${borderSoft}`, borderLeft: '3px solid var(--alert-warning, #f59e0b)', borderRadius: 6 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--alert-warning, #f59e0b)' }}>Verificación requerida.</strong> {challenge.hint || 'Ingresá el código que IG/FB te envió por email/SMS.'} El navegador del server está esperando.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <TechInput
                placeholder="código de verificación"
                value={verifCode}
                onChange={(e) => setVerifCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitCode(); }}
                style={{ flex: '1 1 160px' }}
              />
              <TechButton type="button" onClick={handleSubmitCode} disabled={codeBusy || !verifCode.trim()}>
                {codeBusy ? 'Enviando…' : 'Enviar código'}
              </TechButton>
            </div>
          </div>
        )}

        {extractSteps.length > 0 && (
          <div style={{ marginTop: 12, padding: '0.8rem 1rem', background: panelDeep, border: `1px solid ${borderSoft}`, borderRadius: 6 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
              Extracción automática de cookies
            </div>
            <StepProgress steps={extractSteps} status={extractStepStatus} />
          </div>
        )}

        {credMsg && (
          <div style={{ marginTop: 8, fontSize: '0.8rem', color: credMsg.ok ? 'var(--alert-success, #38d39f)' : 'var(--alert-error, #ff5470)' }}>
            {credMsg.text}
          </div>
        )}
      </TechSection>

      {account.platform === 'instagram' && (
        <TechSection
          title="Correo de verificación (IMAP)"
          action={account.has_email_imap ? <StatusPill style={{ color: 'var(--alert-success, #38d39f)' }}>email IMAP ✓</StatusPill> : null}
          style={{ marginBottom: '1.25rem' }}
        >
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.7rem', lineHeight: 1.5 }}>
            Cargá el <strong>correo</strong> de la cuenta y su <strong>app-password IMAP</strong> (no la contraseña de IG). Cuando IG pida un código de verificación, el server <strong>lo lee del inbox y loguea solo</strong> —sin que ingreses nada—.
            {' '}
            <span style={{ color: 'var(--alert-warning, #f59e0b)' }}>Ojo:</span> en Outlook hay que generar un “app-password” (Seguridad → opciones avanzadas) y tener IMAP habilitado.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <TechInput
              placeholder="correo (ej. cuenta@outlook.com)"
              value={emailAddr}
              onChange={(e) => setEmailAddr(e.target.value)}
              style={{ flex: '1 1 240px' }}
            />
            <TechInput
              type="password"
              placeholder={account.has_email_imap ? 'app-password ✓ (vacío = no cambiar)' : 'app-password IMAP'}
              value={emailImapPass}
              onChange={(e) => setEmailImapPass(e.target.value)}
              style={{ flex: '1 1 200px' }}
            />
            <TechInput
              placeholder="host IMAP (opcional, se infiere)"
              value={emailImapHost}
              onChange={(e) => setEmailImapHost(e.target.value)}
              style={{ flex: '1 1 200px' }}
              title="Dejá vacío para inferirlo del dominio (outlook.office365.com, imap.gmail.com, …)."
            />
            <TechButton type="button" onClick={handleSaveEmail} disabled={savingEmail}>
              <FaKey /> {savingEmail ? 'Guardando…' : 'Guardar correo'}
            </TechButton>
          </div>
          {emailMsg && (
            <div style={{ marginTop: 8, fontSize: '0.8rem', color: emailMsg.ok ? 'var(--alert-success, #38d39f)' : 'var(--alert-error, #ff5470)' }}>
              {emailMsg.text}
            </div>
          )}
        </TechSection>
      )}

      {account.platform === 'facebook' && (
        <div style={{
          padding: '0.7rem 0.9rem',
          background: panelDeep,
          border: `1px solid ${borderSoft}`,
          borderLeft: '3px solid var(--alert-warning, #f59e0b)',
          borderRadius: 6,
          marginBottom: '1rem',
          fontSize: '0.82rem',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}>
          <strong style={{ color: 'var(--alert-warning, #f59e0b)' }}>Facebook · lectura + escritura</strong> · vía <code style={{ fontFamily: mono, color: accent }}>m.facebook.com</code> /  <code style={{ fontFamily: mono, color: accent }}>mbasic</code>.
          Rate-limits FB <strong>aún más conservadores que IG</strong> (3 likes / 2 follows / 1 comment por día).
          FB cambia el HTML seguido — si una acción falla con "link/form no encontrado" significa que mbasic cambió.
        </div>
      )}
      {account.platform === 'linkedin' && (
        <div style={{
          padding: '0.7rem 0.9rem',
          background: panelDeep,
          border: `1px solid ${borderSoft}`,
          borderLeft: '3px solid var(--alert-warning, #f59e0b)',
          borderRadius: 6,
          marginBottom: '1rem',
          fontSize: '0.82rem',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}>
          <strong style={{ color: 'var(--alert-warning, #f59e0b)' }}>LinkedIn · solo snapshots</strong> · al cargar
          cookies (li_at + JSESSIONID), Playwright trae el follower count del perfil. <strong>Las acciones de
          escritura (like, comment, follow) NO están implementadas</strong> — LinkedIn detecta scraping
          agresivamente y banea cuentas en horas.
        </div>
      )}
      {['x', 'twitter'].includes(account.platform) && (
        <div style={{
          padding: '0.85rem 1rem',
          background: panelDeep,
          border: `1px solid ${borderSoft}`,
          borderLeft: '3px solid var(--alert-warning, #f59e0b)',
          borderRadius: 6,
          marginBottom: '1rem',
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}>
          <strong style={{ color: 'var(--alert-warning, #f59e0b)' }}>X / Twitter · lectura + escritura</strong> · login
          programático (flow API) o manual (<code style={{ fontFamily: mono, color: accent }}>auth_token + ct0</code>).
          Acciones: visit feed, simular sesión, visitar perfil, like, unlike, retweet, follow/unfollow.
          Rate-limits ultra conservadores (5 likes / 3 RT / 3 follows por día). <strong>Estudio de ciberseguridad</strong>.
        </div>
      )}
      {!['instagram', 'facebook', 'linkedin', 'x', 'twitter'].includes(account.platform) && (
        <div style={{
          padding: '0.85rem 1rem',
          background: panelDeep,
          border: `1px solid ${borderSoft}`,
          borderLeft: '3px solid var(--alert-error)',
          borderRadius: 6,
          marginBottom: '1rem',
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
        }}>
          <strong style={{ color: 'var(--alert-error)' }}>Platform "{account.platform}" no soportada</strong>
        </div>
      )}

      <QuickActions>
        <TechButton type="button" disabled={!account.has_sessionid || actionBusy === 'visit_feed' || !['instagram', 'facebook', 'x', 'twitter'].includes(account.platform)} onClick={() => handleQuickAction('visit_feed')}>
          <FaPlay /> {actionBusy === 'visit_feed' ? '…' : 'Visitar feed'}
        </TechButton>
        <TechButton type="button" disabled={!account.has_sessionid || actionBusy === 'simulate_session' || !['instagram', 'facebook', 'x', 'twitter'].includes(account.platform)} onClick={() => handleQuickAction('simulate_session')}>
          <FaPlay /> {actionBusy === 'simulate_session' ? '…' : 'Simular sesión humana'}
        </TechButton>
        <TechButtonGhost type="button" disabled={!account.has_sessionid || !['instagram', 'facebook', 'x', 'twitter'].includes(account.platform)} onClick={() => {
          const label = ['x', 'twitter'].includes(account.platform) ? 'Username de X a visitar (ej. elaborador):' : account.platform === 'facebook' ? 'Página/usuario FB a visitar (ej. diarioelnortesn):' : 'Username del perfil a visitar:';
          const t = window.prompt(label);
          if (t) handleQuickAction('visit_profile', t.trim().replace(/^@/, ''));
        }}>
          <FaUser /> Visitar perfil…
        </TechButtonGhost>
      </QuickActions>

      <TechTabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'activity' && (
        <TechSection
          title={`Últimas ${activity.length} acciones`}
          action={
            <TechButtonGhost
              type="button"
              onClick={() => setOnlyErrors((v) => !v)}
              style={onlyErrors ? { color: 'var(--alert-error, #ff5470)', borderColor: 'var(--alert-error, #ff5470)' } : undefined}
            >
              <FaExclamationTriangle /> {onlyErrors ? 'Ver todo' : `Solo errores (${errorCount})`}
            </TechButtonGhost>
          }
        >
          {activity.length === 0 ? (
            <Empty>Sin actividad registrada todavía. Usá los botones de arriba o el chatbot.</Empty>
          ) : shownActivity.length === 0 ? (
            <Empty>Sin errores en las últimas {activity.length} acciones.</Empty>
          ) : (
            <ActivityList>
              {shownActivity.map((a) => (
                <ActivityRow key={a.id} $color={
                  a.status === 'ok' ? 'var(--alert-success, #38d39f)' :
                  a.status === 'error' ? 'var(--alert-error)' : 'var(--alert-warning, #f59e0b)'
                }>
                  <span className="when" title={a.created_at}>{fmtRel(a.created_at)}</span>
                  <span className="action">{a.action}</span>
                  <span className="target" title={a.target}>{a.target || '—'}</span>
                  <span className="source">{a.source}</span>
                  <span className={`status ${a.status}`}>{a.status}</span>
                  {a.error_message && (
                    <span className="errmsg" title="error_message">
                      ⚠ {a.error_message}
                      {a.duration_ms != null ? `  ·  ${a.duration_ms}ms` : ''}
                    </span>
                  )}
                </ActivityRow>
              ))}
            </ActivityList>
          )}
        </TechSection>
      )}

      {tab === 'tasks' && (
        <TechSection
          title={`Tareas del agente (${tasks.length})`}
          action={
            <>
              <TechButtonGhost type="button" onClick={loadTasks}><FaSyncAlt /> Refrescar</TechButtonGhost>
              {tasks.some((t) => ['done', 'failed', 'skipped', 'cancelled'].includes(t.status)) && (
                <TechButtonGhost type="button" onClick={handleClearTasks}><FaTrashAlt /> Limpiar terminadas</TechButtonGhost>
              )}
            </>
          }
        >
          {tasks.length === 0 ? (
            <Empty>Sin tareas todavía. Pedíle algo al agente en la pestaña Chatbot y se van encolando acá (se ejecutan solas, respetando los límites anti-ban).</Empty>
          ) : (
            <ActivityList>
              {tasks.map((t) => {
                const tgt = t.args?.username || t.args?.url || t.args?.text || '—';
                const color =
                  t.status === 'done' ? 'var(--alert-success, #38d39f)' :
                  t.status === 'failed' ? 'var(--alert-error)' :
                  t.status === 'skipped' ? 'var(--alert-warning, #f59e0b)' :
                  t.status === 'running' ? accent : 'var(--text-disabled)';
                const statusClass =
                  t.status === 'done' ? 'ok' :
                  t.status === 'failed' ? 'error' :
                  t.status === 'skipped' ? 'skipped' : '';
                return (
                  <ActivityRow key={t.id} $color={color}>
                    <span className="when" title={t.created_at}>{fmtRel(t.created_at)}</span>
                    <span className="action">{t.action}</span>
                    <span className="target" title={typeof tgt === 'string' ? tgt : ''}>{tgt}</span>
                    <span className="source">{t.source}</span>
                    <span className={`status ${statusClass}`}>{t.status}</span>
                    {t.error_message && (
                      <span className="errmsg" title="error_message">⚠ {t.error_message}</span>
                    )}
                  </ActivityRow>
                );
              })}
            </ActivityList>
          )}
        </TechSection>
      )}

      {tab === 'stats' && (
        <TechSection title="Acciones por tipo (últimos 7 días)">
          {stats.length === 0 ? (
            <Empty>Sin actividad en los últimos 7 días.</Empty>
          ) : (
            <StatsTable>
              <thead>
                <tr>
                  <th>Acción</th>
                  <th>Total</th>
                  <th>OK</th>
                  <th>Error</th>
                  <th>Success rate</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((s) => {
                  const rate = s.total > 0 ? Math.round((s.ok / s.total) * 100) : 0;
                  return (
                    <tr key={s.action}>
                      <td>{s.action}</td>
                      <td>{s.total}</td>
                      <td className="ok">{s.ok}</td>
                      <td className={s.error > 0 ? 'err' : ''}>{s.error}</td>
                      <td className={rate >= 90 ? 'ok' : rate < 60 ? 'err' : ''}>{rate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </StatsTable>
          )}
        </TechSection>
      )}

      {tab === 'chat' && (
        <TechSection
          title="Chatbot del perfil"
          action={<StatusPill>La IA interpreta tus instrucciones</StatusPill>}
        >
          <ChatBox>
            {chatMessages.length === 0 ? (
              <div style={{ color: 'var(--text-disabled)', fontSize: '0.85rem', padding: '1.5rem', textAlign: 'center' }}>
                Pedile al perfil que haga algo. Ejemplos:<br />
                <code style={{ color: accent, fontFamily: mono, fontSize: '0.8rem' }}>visitá el feed</code>
                {' · '}
                <code style={{ color: accent, fontFamily: mono, fontSize: '0.8rem' }}>simulá sesión humana</code>
                {' · '}
                <code style={{ color: accent, fontFamily: mono, fontSize: '0.8rem' }}>visitá @periodismosn</code>
                {' · '}
                <code style={{ color: accent, fontFamily: mono, fontSize: '0.8rem' }}>traé comentarios de https://instagram.com/p/XXX/</code>
              </div>
            ) : (
              chatMessages.map((m, i) => (
                <ChatMsg key={i} $role={m.role}>
                  <span className="icon">{m.role === 'user' ? <FaUser /> : <FaRobot />}</span>
                  <div className="body">
                    <span className="role">{m.role}</span>
                    <div className="content">{m.content}</div>
                    {m.action && (
                      <span className="action">
                        action: {m.action} {m.target ? `→ ${m.target}` : ''} · {m.action_status}
                      </span>
                    )}
                  </div>
                </ChatMsg>
              ))
            )}
            <div ref={chatEndRef} />
          </ChatBox>

          <form onSubmit={handleChat} style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <TechTextarea
              placeholder="Tu instrucción para @${account.username}…"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              rows={2}
              disabled={chatBusy}
              style={{ flex: 1 }}
            />
            <TechButton type="submit" disabled={chatBusy || !chatInput.trim()}>
              <FaPaperPlane /> {chatBusy ? '…' : 'Enviar'}
            </TechButton>
          </form>
        </TechSection>
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        danger
        title="Eliminar perfil"
        message={`¿Eliminar el perfil @${account.username}?\n\nSe borra la cuenta + sessionid + toda su actividad. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        isLoading={deleting}
        onConfirm={handleDelete}
        onCancel={() => { if (!deleting) setConfirmDelete(false); }}
      />
    </TechShell>
  );
};

export default DigitalProfileDetail;
